import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import {
  Coins, ArrowDownToLine, ArrowUpFromLine, Hourglass,
  Plus, X, GitCompare, Settings2, BarChart3, Check, ChevronDown, Radio,
  ListChecks, Receipt, Search,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  Legend, PieChart, Pie, Cell,
} from "recharts";
import { Topbar } from "@/components/dashboard/Topbar";
import { GlassPanel } from "@/components/dashboard/GlassPanel";
import { CreditAmount } from "@/components/genrl/CreditAmount";
import { assignHardwareCostBands, hardwareCostBandLabel } from "@/lib/hardware-cost-band";
import { RouteBadge } from "@/components/genrl/RouteBadge";
import { SettlementStatusPill } from "@/components/genrl/SettlementStatusPill";
import { tooltipStyle, axisTick, gridStroke } from "@/components/dashboard/chartStyle";
import {
  formatMicros,
  MICRO,
  type RouteType,
  SETTLEMENT_LABEL,
  type LedgerRow,
  type SettlementStatus,
  type SettlementRow,
  type PriceIndexRow,
} from "@/lib/genrl";
import { APIError, apiRequest, financeUserID } from "@/lib/api";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

export const Route = createFileRoute("/finance")({
  head: () => ({
    meta: [
      { title: "Finance · GENRL" },
      { name: "description", content: "Track usage vs earnings, compare live cards, and manage your bid pricing." },
    ],
  }),
  validateSearch: (raw: Record<string, unknown>) => ({
    buy: typeof raw.buy === "string" ? raw.buy : undefined,
    stripe_status: typeof raw.stripe_status === "string" ? raw.stripe_status : undefined,
    session_id: typeof raw.session_id === "string" ? raw.session_id : undefined,
    checkout_id: typeof raw.checkout_id === "string" ? raw.checkout_id : undefined,
    hub_id: typeof raw.hub_id === "string" ? raw.hub_id : undefined,
  }),
  component: FinancePage,
});

type TabId = "overview" | "activity" | "settlements" | "compare" | "pricing";

const TONE: Record<string, string> = {
  cyan:    "text-[oklch(0.78_0.16_200)]",
  mint:    "text-[oklch(0.80_0.18_145)]",
  amber:   "text-[oklch(0.82_0.17_80)]",
  violet:  "text-[oklch(0.70_0.18_270)]",
  magenta: "text-[oklch(0.72_0.20_320)]",
};

type BackendCreditAccount = {
  available_credit_micros?: number;
  reserved_credit_micros?: number;
  spent_credit_micros?: number;
  earned_credit_micros?: number;
  pending_payout_credit_micros?: number;
};

type BackendLedgerEntry = {
  id?: string;
  entry_type?: string;
  debit_micros?: number;
  credit_micros?: number;
  reference_type?: string;
  reference_id?: string;
  metadata_json?: string;
  created_at?: string;
};

type BackendPayoutTransfer = {
  id?: string;
  destination_ref?: string;
  amount_micros?: number;
  status?: string;
  updated_at?: string;
  created_at?: string;
};

type BackendPricingRow = {
  provider_name?: string;
  provider_slug?: string;
  hardware_type?: string;
  region?: string;
  route_type?: string;
  buyer_unit_price_micros?: number;
  provider_unit_price_micros?: number;
  genrl_fee_bps?: number;
  stock?: number;
  reliability_score?: number;
  captured_at?: string;
};

type ProviderSourcePublic = {
  id: string;
  source_slug: string;
  display_name: string;
  broadcast_mode?: string;
  pricing_mode?: string;
  provider_adjustment_bps?: number;
  fixed_unit_price_micros?: number;
  min_unit_price_micros?: number;
  reserve_balance_threshold_micros?: number;
  active?: boolean;
};

type StoredAuthUserRef = {
  id: string;
  email: string;
};

function readStoredAuthUserRef(): StoredAuthUserRef {
  if (typeof window === "undefined") return { id: "", email: "" };
  try {
    const raw = window.localStorage.getItem("nebula.auth.user");
    if (!raw) return { id: "", email: "" };
    const parsed = JSON.parse(raw) as { id?: unknown; email?: unknown };
    return {
      id: String(parsed?.id || "").trim(),
      email: String(parsed?.email || "").trim().toLowerCase(),
    };
  } catch {
    return { id: "", email: "" };
  }
}

function mapEntryType(raw: string): LedgerRow["type"] {
  const v = String(raw || "").toUpperCase();
  if (v.includes("PURCHASE")) return "PURCHASE";
  if (v.includes("RESERVE")) return v.includes("RELEASE") ? "REFUND" : "RESERVE";
  if (v.includes("EARNING")) return "EARNING";
  if (v.includes("PAYOUT")) return "PAYOUT";
  if (v.includes("REVENUE") || v.includes("FEE")) return "FEE";
  if (v.includes("REFUND")) return "REFUND";
  return "DEBIT";
}

function mapLedgerRow(row: BackendLedgerEntry): LedgerRow {
  const debit = Number(row.debit_micros || 0);
  const credit = Number(row.credit_micros || 0);
  let routeType: RouteType | undefined;
  try {
    const metadata = JSON.parse(String(row.metadata_json || "{}")) as { route_type?: RouteType };
    if (metadata?.route_type) routeType = metadata.route_type;
  } catch {
    routeType = undefined;
  }
  const referenceType = String(row.reference_type || "").trim();
  const referenceID = String(row.reference_id || "").trim();
  const createdRaw = String(row.created_at || "").trim();
  const fallbackID = [referenceType || "ledger", referenceID || "entry", createdRaw || String(row.entry_type || "event")].join(":");
  return {
    id: String(row.id || fallbackID),
    time: createdRaw ? new Date(createdRaw).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—",
    type: mapEntryType(String(row.entry_type || "")),
    amount_micros: String(credit - debit),
    reference: referenceType && referenceID ? `${referenceType}:${referenceID}` : referenceID || referenceType || "—",
    route_type: routeType,
    status: "POSTED",
  };
}

function mapTransferRow(row: BackendPayoutTransfer): SettlementRow {
  const statusRaw = String(row.status || "").toUpperCase();
  const status: SettlementStatus =
    statusRaw === "SUCCEEDED" ? "PAID" :
    statusRaw === "FAILED" ? "FAILED" :
    "PENDING";
  const amount = Number(row.amount_micros || 0);
  const created = String(row.created_at || "").trim();
  const updated = String(row.updated_at || created).trim();
  const fallbackID = [String(row.destination_ref || "payout"), created || "transfer"].join(":");
  return {
    settlement_id: String(row.id || fallbackID),
    reference: String(row.id || "—"),
    route_type: "PEER",
    provider: String(row.destination_ref || "").trim() || "—",
    reserved_amount_micros: String(amount),
    final_buyer_debit_micros: String(amount),
    provider_earnings_micros: String(amount),
    genrl_revenue_micros: "0",
    refund_amount_micros: "0",
    status,
    updated: updated ? new Date(updated).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—",
  };
}

function mapPricingRow(row: BackendPricingRow): PriceIndexRow {
  const providerUnit = Number(row.provider_unit_price_micros || 0);
  const feeBps = Number(row.genrl_fee_bps || 0);
  const feeMicros = Math.round((providerUnit * feeBps) / 10000);
  const buyerUnit = Number(row.buyer_unit_price_micros || providerUnit + feeMicros);
  const capturedAt = String(row.captured_at || "").trim();
  return {
    provider: String(row.provider_name || "").trim() || "—",
    route_type: (String(row.route_type || "PEER").toUpperCase() as RouteType),
    region: String(row.region || "").trim() || "—",
    hardware: String(row.hardware_type || "").trim() || "—",
    stock: Math.max(0, Number(row.stock || 0)),
    raw_unit_price_micros: String(providerUnit),
    provider_unit_price_micros: String(providerUnit),
    genrl_unit_fee_micros: String(feeMicros),
    buyer_unit_price_micros: String(buyerUnit),
    reliability: Number(row.reliability_score || 0),
    updated: capturedAt ? new Date(capturedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—",
  };
}

function FinancePage() {
  const { buy, stripe_status: stripeStatus } = Route.useSearch();
  const [tab, setTab] = useState<TabId>("overview");
  const [payoutOpen, setPayoutOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [account, setAccount] = useState<BackendCreditAccount>({});
  const [ledgerRows, setLedgerRows] = useState<LedgerRow[]>([]);
  const [settlementRows, setSettlementRows] = useState<SettlementRow[]>([]);
  const [priceRows, setPriceRows] = useState<PriceIndexRow[]>([]);
  const [providerSources, setProviderSources] = useState<ProviderSourcePublic[]>([]);
  const storedUser = useMemo(() => readStoredAuthUserRef(), []);
  const userID = financeUserID(storedUser.id || "");

  async function refreshFinance() {
    setLoading(true);
    try {
      let balancePayload: BackendCreditAccount;
      let ledgerPayload: { entries?: BackendLedgerEntry[] };
      try {
        [balancePayload, ledgerPayload] = await Promise.all([
          apiRequest<BackendCreditAccount>("/api/wallet/balance", { method: "GET", timeoutMs: 12000 }),
          apiRequest<{ entries?: BackendLedgerEntry[] }>("/api/wallet/ledger?limit=200", { method: "GET", timeoutMs: 12000 }),
        ]);
      } catch {
        const balancePath = userID
          ? `/api/v1/credits/balance?user_id=${encodeURIComponent(userID)}`
          : "/api/v1/credits/balance";
        const ledgerPath = userID
          ? `/api/v1/credits/ledger?user_id=${encodeURIComponent(userID)}&limit=200`
          : "/api/v1/credits/ledger?limit=200";
        [balancePayload, ledgerPayload] = await Promise.all([
          apiRequest<BackendCreditAccount>(balancePath, { method: "GET", timeoutMs: 12000 }),
          apiRequest<{ entries?: BackendLedgerEntry[] }>(ledgerPath, { method: "GET", timeoutMs: 12000 }),
        ]);
      }
      const payoutPath = userID
        ? `/api/v1/payout/transfers?user_id=${encodeURIComponent(userID)}&limit=200`
        : "/api/v1/payout/transfers?limit=200";
      const [transfersPayload, comparePayload, sourcesPayload] = await Promise.all([
        apiRequest<{ transfers?: BackendPayoutTransfer[] }>(payoutPath, { method: "GET", timeoutMs: 12000 }).catch(() => ({ transfers: [] })),
        apiRequest<{ rows?: BackendPricingRow[] }>("/api/v1/pricing/compare?limit=120", { method: "GET", timeoutMs: 15000 }).catch(() => ({ rows: [] })),
        apiRequest<{ sources?: ProviderSourcePublic[] }>("/admin/v1/local/provider-sources", { method: "GET", timeoutMs: 12000 }).catch(() => ({ sources: [] })),
      ]);

      setAccount({
        available_credit_micros: Number(balancePayload.available_credit_micros || 0),
        reserved_credit_micros: Number(balancePayload.reserved_credit_micros || 0),
        spent_credit_micros: Number(balancePayload.spent_credit_micros || 0),
        earned_credit_micros: Number(balancePayload.earned_credit_micros || 0),
        pending_payout_credit_micros: Number(balancePayload.pending_payout_credit_micros || 0),
      });

      const nextLedger = (Array.isArray(ledgerPayload.entries) ? ledgerPayload.entries : []).map(mapLedgerRow);
      setLedgerRows(nextLedger);

      const nextSettlements = (Array.isArray(transfersPayload.transfers) ? transfersPayload.transfers : []).map(mapTransferRow);
      setSettlementRows(nextSettlements);

      const nextPrices = (Array.isArray(comparePayload.rows) ? comparePayload.rows : []).map(mapPricingRow);
      setPriceRows(nextPrices);
      setProviderSources(Array.isArray(sourcesPayload.sources) ? sourcesPayload.sources : []);
    } catch (err) {
      if (err instanceof APIError) toast.error(err.detail || "Failed to refresh finance");
      else toast.error("Failed to refresh finance");
      setAccount({});
      setLedgerRows([]);
      setSettlementRows([]);
      setPriceRows([]);
      setProviderSources([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refreshFinance();
  }, []);

  useEffect(() => {
    if (!buy && !stripeStatus) return;
    window.location.assign("/settings?section=billing");
  }, [buy, stripeStatus]);

  const tabs: { id: TabId; label: string; icon: LucideIcon }[] = [
    { id: "overview", label: "Overview",   icon: BarChart3 },
    { id: "activity", label: "Activity",   icon: ListChecks },
    { id: "settlements", label: "Settlements", icon: Receipt },
    { id: "compare",  label: "Compare",    icon: GitCompare },
    { id: "pricing",  label: "My Pricing", icon: Settings2 },
  ];

  return (
    <>
      <Topbar
        title="Finance"
        subtitle="Usage vs earnings, live card comparisons, and your bid pricing — at a glance."
      />
      <main className="flex-1 space-y-6 p-6">
        {/* Compact balance strip */}
        <section className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <KpiCard tone="cyan"   icon={Coins}           label="Available"  value={<CreditAmount micros={account.available_credit_micros || 0} suffix="" />} cta="Buy credits"     onClick={() => window.location.assign("/settings?section=billing")} />
          <KpiCard tone="mint"   icon={ArrowUpFromLine} label="Earnings"   value={<CreditAmount micros={account.earned_credit_micros || 0} suffix="" />}    cta="Request payout"  onClick={() => setPayoutOpen(true)} />
          <KpiCard tone="amber"  icon={Hourglass}       label="Reserved"   value={<CreditAmount micros={account.reserved_credit_micros || 0} suffix="" />}  hint="locked for active jobs" />
          <KpiCard tone="violet" icon={ArrowDownToLine} label="Spent"      value={<CreditAmount micros={account.spent_credit_micros || 0} suffix="" />}     hint="lifetime" />
        </section>

        {/* Tab chips */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-1 rounded-full border hairline bg-soft p-1 w-fit">
            {tabs.map((t) => {
              const active = tab === t.id;
              const Icon = t.icon;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-medium transition",
                    active ? "text-white shadow-[var(--shadow-glow)]" : "text-muted-foreground hover:text-foreground",
                  )}
                  style={active ? { background: "var(--gradient-primary)" } : undefined}
                >
                  <Icon className="h-3 w-3" /> {t.label}
                </button>
              );
            })}
          </div>
          <button
            onClick={() => void refreshFinance()}
            disabled={loading}
            className="rounded-full border hairline bg-soft px-3 py-1.5 text-[11px] text-muted-foreground hover:text-foreground disabled:opacity-50"
          >
            Refresh
          </button>
        </div>

        {tab === "overview" && <OverviewTab account={account} ledgerRows={ledgerRows} settlementRows={settlementRows} priceRows={priceRows} />}
        {tab === "activity" && <ActivityTab rows={ledgerRows} />}
        {tab === "settlements" && <SettlementsTab rows={settlementRows} />}
        {tab === "compare"  && <CompareTab rows={priceRows} />}
        {tab === "pricing"  && <PricingTab sources={providerSources} />}
      </main>

      {payoutOpen && (
        <RequestPayoutModal
          userID={userID}
          maxMicros={Number(account.pending_payout_credit_micros || 0)}
          onClose={() => setPayoutOpen(false)}
          onSuccess={async () => {
            setPayoutOpen(false);
            await refreshFinance();
          }}
        />
      )}
    </>
  );
}

/* ----------------------- KPI ----------------------- */

function KpiCard({
  tone, icon: Icon, label, value, hint, onClick, cta,
}: {
  tone: keyof typeof TONE;
  icon: LucideIcon;
  label: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
  onClick?: () => void;
  cta?: string;
}) {
  return (
    <div className="glass relative overflow-hidden rounded-2xl p-5">
      <div aria-hidden className="pointer-events-none absolute -top-12 -right-12 h-32 w-32 rounded-full opacity-25 blur-2xl" style={{ background: "var(--gradient-primary)" }} />
      <div className="relative flex items-start justify-between">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">{label}</p>
          <p className="mt-2 font-display text-2xl font-semibold tracking-tight">{value}</p>
          {hint && <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p>}
        </div>
        <div className={cn("flex h-9 w-9 items-center justify-center rounded-lg border hairline bg-soft", TONE[tone])}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
      {onClick && cta && (
        <button onClick={onClick} className="relative mt-3 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline">
          {cta} →
        </button>
      )}
    </div>
  );
}

/* ----------------------- Overview (charts) ----------------------- */

function OverviewTab({
  account,
  ledgerRows,
  settlementRows,
  priceRows,
}: {
  account: BackendCreditAccount;
  ledgerRows: LedgerRow[];
  settlementRows: SettlementRow[];
  priceRows: PriceIndexRow[];
}) {
  const usageVsEarnings = useMemo(() => {
    const recent = ledgerRows.slice(0, 7).reverse();
    return recent.map((row, idx) => {
      const amount = Number(row.amount_micros || 0) / MICRO;
      return {
        day: row.time || `T${idx + 1}`,
        usage: amount < 0 ? Math.abs(amount) : 0,
        earnings: amount > 0 ? amount : 0,
      };
    });
  }, [ledgerRows]);

  const balanceTrend = useMemo(() => {
    const recent = ledgerRows.slice(0, 7).reverse();
    const deltas = recent.map((row) => Number(row.amount_micros || 0) / MICRO);
    const current = Number(account.available_credit_micros || 0) / MICRO;
    const start = current - deltas.reduce((sum, v) => sum + v, 0);
    let running = start;
    return recent.map((row, idx) => {
      running += deltas[idx] || 0;
      return { day: row.time || `T${idx + 1}`, balance: Number(running.toFixed(2)) };
    });
  }, [ledgerRows, account.available_credit_micros]);

  const routeMix = useMemo(() => {
    const totals = new Map<string, number>();
    settlementRows.forEach((row) => {
      const key = String(row.route_type || "PEER");
      const debit = Number(row.final_buyer_debit_micros || 0) / MICRO;
      totals.set(key, (totals.get(key) || 0) + Math.max(debit, 0));
    });
    const colors: Record<string, string> = {
      PEER: "oklch(0.72 0.20 320)",
      INTERNAL: "oklch(0.78 0.16 200)",
      GENRL_NATIVE: "oklch(0.80 0.18 145)",
      HYBRID: "oklch(0.82 0.17 80)",
    };
    return [...totals.entries()].map(([name, value]) => ({
      name: name.replaceAll("_", " "),
      value: Number(value.toFixed(2)),
      color: colors[name] || "oklch(0.70 0.18 270)",
    }));
  }, [settlementRows]);

  const hardwareSupply = useMemo(() => {
    const grouped = new Map<string, { stock: number; bestBuyerUnitMicros: number }>();
    priceRows.forEach((row) => {
      const key = String(row.hardware || "GPU");
      const buyerUnitMicros = Math.max(0, Number(row.buyer_unit_price_micros || 0));
      const current = grouped.get(key) || { stock: 0, bestBuyerUnitMicros: 0 };
      current.stock += Math.max(0, Number(row.stock || 0));
      if (
        buyerUnitMicros > 0 &&
        (current.bestBuyerUnitMicros === 0 || buyerUnitMicros < current.bestBuyerUnitMicros)
      ) {
        current.bestBuyerUnitMicros = buyerUnitMicros;
      }
      grouped.set(key, current);
    });
    const rows = [...grouped.entries()].map(([hardware, values]) => ({
      hardware,
      stock: values.stock,
      bestBuyerUnitMicros: values.bestBuyerUnitMicros,
    }));
    const costBands = assignHardwareCostBands(rows, (row) => row.bestBuyerUnitMicros);
    return rows
      .map((row, index) => ({
        ...row,
        costBand: hardwareCostBandLabel(costBands[index] || "Mid cost"),
      }))
      .sort((a, b) => b.stock - a.stock || a.hardware.localeCompare(b.hardware))
      .slice(0, 5);
  }, [priceRows]);

  const totalUsage = usageVsEarnings.reduce((a, r) => a + r.usage, 0);
  const totalEarnings = usageVsEarnings.reduce((a, r) => a + r.earnings, 0);
  const net           = totalEarnings - totalUsage;

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      <GlassPanel
        title="Usage vs earnings"
        subtitle="Last 7 days · credits"
        className="lg:col-span-2"
        action={
          <div className="flex gap-3 text-[11px]">
            <span className="text-muted-foreground">Spent <span className="font-mono text-foreground">{totalUsage}</span></span>
            <span className="text-muted-foreground">Earned <span className="font-mono text-foreground">{totalEarnings}</span></span>
            <span className={cn("font-mono", net >= 0 ? "text-[var(--success)]" : "text-destructive")}>
              {net >= 0 ? "+" : ""}{net}
            </span>
          </div>
        }
      >
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={usageVsEarnings} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
              <defs>
                <linearGradient id="gUsage" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"  stopColor="oklch(0.70 0.18 270)" stopOpacity={0.55} />
                  <stop offset="100%" stopColor="oklch(0.70 0.18 270)" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gEarn" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"  stopColor="oklch(0.80 0.18 145)" stopOpacity={0.55} />
                  <stop offset="100%" stopColor="oklch(0.80 0.18 145)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke={gridStroke} strokeDasharray="3 6" vertical={false} />
              <XAxis dataKey="day" tick={axisTick} axisLine={false} tickLine={false} />
              <YAxis tick={axisTick} axisLine={false} tickLine={false} width={40} />
              <Tooltip {...tooltipStyle} />
              <Area type="monotone" dataKey="usage"    name="Usage"    stroke="oklch(0.70 0.18 270)" fill="url(#gUsage)" strokeWidth={2} />
              <Area type="monotone" dataKey="earnings" name="Earnings" stroke="oklch(0.80 0.18 145)" fill="url(#gEarn)"  strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </GlassPanel>

      <GlassPanel title="Route mix" subtitle="Where your spend went">
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={routeMix} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={2} stroke="none">
                {routeMix.map((s) => <Cell key={s.name} fill={s.color} />)}
              </Pie>
              <Tooltip {...tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </GlassPanel>

      <GlassPanel title="Balance trend" subtitle="Last 7 days · available credits" className="lg:col-span-2">
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={balanceTrend} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
              <defs>
                <linearGradient id="gBal" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"  stopColor="oklch(0.78 0.16 200)" stopOpacity={0.55} />
                  <stop offset="100%" stopColor="oklch(0.78 0.16 200)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke={gridStroke} strokeDasharray="3 6" vertical={false} />
              <XAxis dataKey="day" tick={axisTick} axisLine={false} tickLine={false} />
              <YAxis tick={axisTick} axisLine={false} tickLine={false} width={50} />
              <Tooltip {...tooltipStyle} />
              <Area type="monotone" dataKey="balance" stroke="oklch(0.78 0.16 200)" fill="url(#gBal)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </GlassPanel>

      <GlassPanel title="Hardware supply by cost band" subtitle="Relative only · exact rates hidden">
        <div className="grid min-h-56 content-start gap-px bg-border">
          {hardwareSupply.map((row) => (
            <div
              key={row.hardware}
              className="flex items-center justify-between gap-4 bg-background px-4 py-3"
            >
              <div>
                <p className="text-[12px] font-semibold">{row.hardware}</p>
                <p className="mt-1 text-[10px] text-muted-foreground">
                  {row.stock.toLocaleString()} available
                </p>
              </div>
              <span className="font-mono text-[11px] font-semibold">{row.costBand}</span>
            </div>
          ))}
          {hardwareSupply.length === 0 ? (
            <p className="bg-background px-4 py-10 text-center text-[12px] text-muted-foreground">
              No live hardware supply.
            </p>
          ) : null}
        </div>
      </GlassPanel>
    </div>
  );
}

/* ----------------------- Compare (multi-card) ----------------------- */

function CompareTab({ rows }: { rows: PriceIndexRow[] }) {
  const hardwares = useMemo(
    () => Array.from(new Set(rows.map((p) => p.hardware).filter((v) => String(v || "").trim() !== ""))),
    [rows],
  );
  const [hardware, setHardware] = useState<string>(hardwares[0] || "");
  const [selected, setSelected] = useState<string[]>([]);

  useEffect(() => {
    if (hardwares.length === 0) {
      setHardware("");
      return;
    }
    if (!hardwares.includes(hardware)) {
      setHardware(hardwares[0]);
      setSelected([]);
    }
  }, [hardwares, hardware]);

  const offers = useMemo(
    () => rows
      .filter((p) => (hardware ? p.hardware === hardware : true))
      .sort((a, b) => Number(a.buyer_unit_price_micros) - Number(b.buyer_unit_price_micros)),
    [rows, hardware],
  );
  const offerCostBands = useMemo(
    () => assignHardwareCostBands(offers, (offer) => Number(offer.buyer_unit_price_micros)),
    [offers],
  );

  const toggle = (key: string) => {
    setSelected((s) => s.includes(key) ? s.filter((x) => x !== key) : s.length >= 4 ? s : [...s, key]);
  };
  const keyOf = (i: number, p: PriceIndexRow) => `${p.provider}-${p.region}-${i}`;

  const compared = offers
    .map((p, i) => ({
      ...p,
      _key: keyOf(i, p),
      _costBand: offerCostBands[i] || "Mid cost",
    }))
    .filter((p) => selected.includes(p._key));

  return (
    <div className="space-y-6">
      <GlassPanel
        title="Live comparison"
        subtitle={`Pick up to 4 cards to compare side-by-side · ${offers.length} offers for ${hardware}`}
        action={
          <div className="flex flex-wrap gap-1">
            {hardwares.map((h) => (
              <button
                key={h}
                onClick={() => { setHardware(h); setSelected([]); }}
                className={cn(
                  "rounded-full border px-2.5 py-1 text-[11px]",
                  hardware === h ? "border-primary bg-primary/10 text-primary" : "hairline bg-soft text-muted-foreground hover:text-foreground",
                )}
              >
                {h}
              </button>
            ))}
          </div>
        }
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {offers.map((p, i) => {
            const k = keyOf(i, p);
            const picked = selected.includes(k);
            const costBand = hardwareCostBandLabel(offerCostBands[i] || "Mid cost");
            return (
              <button
                key={k}
                onClick={() => toggle(k)}
                className={cn(
                  "relative rounded-2xl border bg-soft p-4 text-left transition hover:-translate-y-0.5",
                  picked ? "border-primary shadow-[var(--shadow-glow)]" : "hairline",
                )}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold">{p.provider}</p>
                    <p className="text-[11px] text-muted-foreground">{p.region} · stock {p.stock}</p>
                  </div>
                  <div className={cn(
                    "flex h-5 w-5 items-center justify-center rounded-md border",
                    picked ? "border-primary bg-primary text-white" : "hairline bg-background text-transparent",
                  )}>
                    <Check className="h-3 w-3" />
                  </div>
                </div>
                <div className="mt-3">
                  <RouteBadge routeType={p.route_type} />
                </div>
                <p className="mt-2 font-display text-xl font-semibold tracking-tight">
                  {costBand}
                </p>
                <p className="text-[10px] text-muted-foreground">
                  reliability {(p.reliability * 100).toFixed(0)}%
                </p>
              </button>
            );
          })}
        </div>
      </GlassPanel>

      {compared.length >= 2 && (
        <GlassPanel title="Side-by-side" subtitle={`${compared.length} cards selected`}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b hairline text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                  <th className="px-2 py-2">Metric</th>
                  {compared.map((p) => (
                    <th key={p._key} className="px-2 py-2 text-right">{p.provider}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="[&_tr]:border-b [&_tr]:hairline [&_tr:last-child]:border-0">
                <CompareRow label="Route"          cells={compared.map((p) => <RouteBadge routeType={p.route_type} />)} />
                <CompareRow label="Region"         cells={compared.map((p) => <span className="text-xs text-muted-foreground">{p.region}</span>)} />
                <CompareRow label="Stock"          cells={compared.map((p) => <span className="font-mono text-xs">{p.stock}</span>)} />
                <CompareRow label="Cost band"      bold cells={compared.map((p) => <span>{hardwareCostBandLabel(p._costBand)}</span>)} />
                <CompareRow label="Reliability"    cells={compared.map((p) => <span className="text-xs">{(p.reliability * 100).toFixed(0)}%</span>)} />
              </tbody>
            </table>
          </div>
        </GlassPanel>
      )}
    </div>
  );
}

function CompareRow({ label, cells, bold }: { label: string; cells: React.ReactNode[]; bold?: boolean }) {
  return (
    <tr>
      <td className="px-2 py-2 text-[11px] uppercase tracking-wider text-muted-foreground">{label}</td>
      {cells.map((c, i) => (
        <td key={i} className={cn("px-2 py-2 text-right", bold && "font-display font-semibold")}>{c}</td>
      ))}
    </tr>
  );
}

/* ----------------------- My Pricing ----------------------- */

function PricingTab({ sources }: { sources: ProviderSourcePublic[] }) {
  const totals = useMemo(() => {
    const active = sources.filter((s) => s.active !== false);
    const broadcasting = active.filter((s) => String(s.broadcast_mode || "BOTH").toUpperCase() !== "INTERNAL_ONLY").length;
    const projected = active.reduce((sum, row) => {
      const unit = Number(row.fixed_unit_price_micros || row.min_unit_price_micros || 0);
      return sum + (unit > 0 ? unit : 0);
    }, 0);
    return { total: sources.length, active: active.length, broadcasting, projected };
  }, [sources]);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
        <SummaryTile label="Configured sources" value={totals.total} />
        <SummaryTile label="Active sources" value={totals.active} />
        <SummaryTile label="Broadcasting" value={totals.broadcasting} />
        <SummaryTile label="Min quoted / unit" value={<CreditAmount micros={totals.projected} suffix="" />} />
      </div>

      <GlassPanel
        title="Live pricing policy"
        subtitle="Sourced directly from local provider-source configuration"
      >
        {sources.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground">
            No provider sources configured yet. Add sources in the Providers page.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] text-xs">
              <thead>
                <tr className="border-b hairline text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  <th className="px-3 py-2">Source</th>
                  <th className="px-3 py-2">Mode</th>
                  <th className="px-3 py-2">Pricing</th>
                  <th className="px-3 py-2 text-right">Adjustment</th>
                  <th className="px-3 py-2 text-right">Floor</th>
                  <th className="px-3 py-2 text-right">Fixed</th>
                  <th className="px-3 py-2 text-right">Reserve threshold</th>
                  <th className="px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {sources.map((row) => {
                  const mode = String(row.broadcast_mode || "BOTH").toUpperCase();
                  const pricing = String(row.pricing_mode || "PASSTHROUGH").toUpperCase();
                  const adjustmentBps = Number(row.provider_adjustment_bps || 0);
                  const fixedMicros = Number(row.fixed_unit_price_micros || 0);
                  const floorMicros = Number(row.min_unit_price_micros || 0);
                  const thresholdMicros = Number(row.reserve_balance_threshold_micros || 0);
                  return (
                    <tr key={row.id} className="border-t hairline">
                      <td className="px-3 py-2">
                        <p className="font-medium">{row.display_name || row.source_slug}</p>
                        <p className="font-mono text-[10px] text-muted-foreground">{row.source_slug}</p>
                      </td>
                      <td className="px-3 py-2">{mode.replaceAll("_", " ").toLowerCase()}</td>
                      <td className="px-3 py-2">{pricing.replaceAll("_", " ").toLowerCase()}</td>
                      <td className={cn(
                        "px-3 py-2 text-right font-mono",
                        adjustmentBps >= 0 ? "text-[var(--success)]" : "text-destructive",
                      )}
                      >
                        {adjustmentBps >= 0 ? "+" : ""}{(adjustmentBps / 100).toFixed(2)}%
                      </td>
                      <td className="px-3 py-2 text-right font-mono">{floorMicros > 0 ? formatMicros(floorMicros, { suffix: "" }) : "—"}</td>
                      <td className="px-3 py-2 text-right font-mono">{fixedMicros > 0 ? formatMicros(fixedMicros, { suffix: "" }) : "—"}</td>
                      <td className="px-3 py-2 text-right font-mono">{thresholdMicros > 0 ? formatMicros(thresholdMicros, { suffix: "" }) : "—"}</td>
                      <td className="px-3 py-2">
                        <span className={cn(
                          "inline-flex rounded-full border px-2 py-0.5 text-[10px] font-medium",
                          row.active !== false
                            ? "border-[var(--success)]/30 bg-[var(--success)]/10 text-[var(--success)]"
                            : "border-[var(--warning)]/30 bg-[var(--warning)]/10 text-[var(--warning)]",
                        )}
                        >
                          {row.active !== false ? "active" : "paused"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </GlassPanel>
    </div>
  );
}

function SummaryTile({ label, value, tone }: { label: string; value: React.ReactNode; tone?: keyof typeof TONE }) {
  return (
    <div className="glass rounded-2xl p-4">
      <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">{label}</p>
      <p className={cn("mt-1 font-display text-xl font-semibold tracking-tight", tone && TONE[tone])}>{value}</p>
    </div>
  );
}

/* ----------------------- Modals ----------------------- */

function ModalShell({ title, subtitle, children, onClose }: { title: string; subtitle?: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/60 backdrop-blur-sm p-4">
      <div className="glass-strong relative w-full max-w-md rounded-2xl p-6">
        <button onClick={onClose} className="absolute right-4 top-4 text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
        <h3 className="font-display text-lg font-semibold tracking-tight">{title}</h3>
        {subtitle && <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>}
        <div className="mt-5">{children}</div>
      </div>
    </div>
  );
}

function RequestPayoutModal({
  userID,
  maxMicros,
  onClose,
  onSuccess,
}: {
  userID: string;
  maxMicros: number;
  onClose: () => void;
  onSuccess: () => Promise<void>;
}) {
  const max = Number(maxMicros || 0) / MICRO;
  const [amount, setAmount] = useState(max);
  const [busy, setBusy] = useState(false);

  async function submitPayout() {
    setBusy(true);
    try {
      await apiRequest("/api/v1/payout/withdraw", {
        method: "POST",
        body: {
          user_id: userID,
          limit: 1,
        },
        timeoutMs: 15000,
      });
      toast.success("Payout request submitted");
      await onSuccess();
    } catch (err) {
      if (err instanceof APIError) toast.error(err.detail || "Failed to request payout");
      else toast.error("Failed to request payout");
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalShell title="Request payout" subtitle="Convert earned credits to your linked payout account." onClose={onClose}>
      <label className="block text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Amount (credits)</label>
      <input
        type="number"
        min={0}
        max={max}
        value={amount}
        onChange={(e) => setAmount(Math.min(max, Math.max(0, Number(e.target.value))))}
        className="mt-1 w-full rounded-xl border hairline bg-soft px-3 py-2 text-base font-mono outline-none focus:border-primary/50"
      />
      <p className="mt-1 text-[11px] text-muted-foreground">Available <CreditAmount micros={maxMicros} muted /></p>
      <button
        onClick={() => { void submitPayout(); }}
        disabled={busy || amount <= 0}
        className="mt-5 w-full rounded-xl py-2.5 text-sm font-semibold text-white shadow-[var(--shadow-glow)] disabled:opacity-50"
        style={{ background: "var(--gradient-primary)" }}
      >
        {busy ? "Requesting…" : "Request payout"}
      </button>
    </ModalShell>
  );
}

/* ----------------------- Activity (Ledger) ----------------------- */

const LEDGER_TYPE_TONE: Record<LedgerRow["type"], string> = {
  PURCHASE: "text-[oklch(0.78_0.16_200)] border-[oklch(0.78_0.16_200)]/30 bg-[oklch(0.78_0.16_200)]/10",
  EARNING:  "text-[oklch(0.80_0.18_145)] border-[oklch(0.80_0.18_145)]/30 bg-[oklch(0.80_0.18_145)]/10",
  REFUND:   "text-[oklch(0.80_0.18_145)] border-[oklch(0.80_0.18_145)]/30 bg-[oklch(0.80_0.18_145)]/10",
  RESERVE:  "text-[oklch(0.82_0.17_80)] border-[oklch(0.82_0.17_80)]/30 bg-[oklch(0.82_0.17_80)]/10",
  DEBIT:    "text-[oklch(0.70_0.18_270)] border-[oklch(0.70_0.18_270)]/30 bg-[oklch(0.70_0.18_270)]/10",
  FEE:      "text-[oklch(0.72_0.20_320)] border-[oklch(0.72_0.20_320)]/30 bg-[oklch(0.72_0.20_320)]/10",
  PAYOUT:   "text-muted-foreground border-border bg-soft",
};

const LEDGER_TYPES: LedgerRow["type"][] = ["PURCHASE", "RESERVE", "DEBIT", "REFUND", "EARNING", "FEE", "PAYOUT"];

function ActivityTab({ rows }: { rows: LedgerRow[] }) {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<LedgerRow["type"] | "ALL">("ALL");

  const filtered = useMemo(() => {
    return rows.filter((row) => {
      if (filter !== "ALL" && row.type !== filter) return false;
      if (query && !row.reference.toLowerCase().includes(query.toLowerCase())) return false;
      return true;
    });
  }, [rows, query, filter]);

  const totals = useMemo(() => {
    let inflow = 0, outflow = 0;
    for (const r of filtered) {
      const v = Number(r.amount_micros);
      if (v >= 0) inflow += v; else outflow += -v;
    }
    return { inflow, outflow, net: inflow - outflow };
  }, [filtered]);

  return (
    <div className="space-y-4">
      {/* Summary strip */}
      <div className="grid grid-cols-3 gap-3">
        <SummaryTile label="Inflow"  value={formatMicros(totals.inflow,  { suffix: "credits" })} tone="mint" />
        <SummaryTile label="Outflow" value={formatMicros(totals.outflow, { suffix: "credits" })} tone="violet" />
        <SummaryTile label="Net"     value={formatMicros(totals.net,     { suffix: "credits", sign: true })} tone={totals.net >= 0 ? "mint" : "amber"} />
      </div>

      <GlassPanel
        title="Ledger"
        subtitle={`${filtered.length} entries · every credit and debit on your account`}
        action={
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search reference…"
                className="h-8 w-56 rounded-full border hairline bg-soft pl-8 pr-3 text-xs outline-none placeholder:text-muted-foreground focus:border-primary/40"
              />
            </div>
          </div>
        }
      >
        {/* Type chips */}
        <div className="mb-3 flex flex-wrap items-center gap-1">
          {(["ALL", ...LEDGER_TYPES] as const).map((t) => {
            const active = filter === t;
            return (
              <button
                key={t}
                onClick={() => setFilter(t)}
                className={cn(
                  "rounded-full border px-2.5 py-0.5 text-[10px] font-medium uppercase tracking-wider transition",
                  active ? "border-primary/40 bg-primary/10 text-primary" : "hairline bg-soft text-muted-foreground hover:text-foreground",
                )}
              >
                {t.toLowerCase()}
              </button>
            );
          })}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-xs">
            <thead>
              <tr className="text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                <th className="px-3 py-2">Time</th>
                <th className="px-3 py-2">Type</th>
                <th className="px-3 py-2">Reference</th>
                <th className="px-3 py-2">Route</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => {
                const v = Number(row.amount_micros);
                const positive = v >= 0;
                return (
                  <tr key={row.id} className="border-t hairline">
                    <td className="px-3 py-2 font-mono text-muted-foreground">{row.time}</td>
                    <td className="px-3 py-2">
                      <span className={cn("inline-flex rounded-full border px-2 py-0.5 text-[10px] font-medium", LEDGER_TYPE_TONE[row.type])}>
                        {row.type.toLowerCase()}
                      </span>
                    </td>
                    <td className="px-3 py-2 font-mono text-[11px] text-foreground">{row.reference}</td>
                    <td className="px-3 py-2">{row.route_type ? <RouteBadge routeType={row.route_type} /> : <span className="text-muted-foreground">—</span>}</td>
                    <td className="px-3 py-2">
                      <span className={cn(
                        "text-[10px] font-medium",
                        row.status === "POSTED" && "text-[var(--success)]",
                        row.status === "PENDING" && "text-[var(--warning)]",
                        row.status === "DISPUTED" && "text-destructive",
                      )}>{row.status.toLowerCase()}</span>
                    </td>
                    <td className={cn(
                      "px-3 py-2 text-right font-mono font-semibold",
                      positive ? "text-[var(--success)]" : "text-foreground",
                    )}>
                      {formatMicros(v, { suffix: "", sign: true })}
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-10 text-center text-muted-foreground">No entries match those filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </GlassPanel>
    </div>
  );
}

/* ----------------------- Settlements ----------------------- */

const SETTLE_FILTERS: (SettlementStatus | "ALL")[] = ["ALL", "PENDING", "METERED", "RECONCILED", "FINALIZED", "PAID", "DISPUTED", "FAILED"];

function SettlementsTab({ rows }: { rows: SettlementRow[] }) {
  const [filter, setFilter] = useState<SettlementStatus | "ALL">("ALL");

  const filteredRows = useMemo(() => {
    return rows.filter((s) => filter === "ALL" || s.status === filter);
  }, [rows, filter]);

  const totals = useMemo(() => {
    let earned = 0, pending = 0, fees = 0;
    for (const s of rows) {
      const earn = Number(s.provider_earnings_micros);
      if (s.status === "PAID" || s.status === "FINALIZED") earned += earn;
      else if (s.status !== "FAILED" && s.status !== "DISPUTED") pending += earn;
      fees += Number(s.genrl_revenue_micros);
    }
    return { earned, pending, fees };
  }, [rows]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <SummaryTile label="Earned · settled" value={formatMicros(totals.earned, { suffix: "credits" })} tone="mint" />
        <SummaryTile label="In flight"        value={formatMicros(totals.pending, { suffix: "credits" })} tone="amber" />
        <SummaryTile label="GENRL fees"       value={formatMicros(totals.fees,    { suffix: "credits" })} tone="violet" />
      </div>

      <GlassPanel
        title="Settlements"
        subtitle={`${filteredRows.length} records · cash flow on payouts and settlements`}
      >
        <div className="mb-3 flex flex-wrap items-center gap-1">
          {SETTLE_FILTERS.map((f) => {
            const active = filter === f;
            return (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={cn(
                  "rounded-full border px-2.5 py-0.5 text-[10px] font-medium uppercase tracking-wider transition",
                  active ? "border-primary/40 bg-primary/10 text-primary" : "hairline bg-soft text-muted-foreground hover:text-foreground",
                )}
              >
                {f === "ALL" ? "all" : SETTLEMENT_LABEL[f].toLowerCase()}
              </button>
            );
          })}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-xs">
            <thead>
              <tr className="text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                <th className="px-3 py-2">Reference</th>
                <th className="px-3 py-2">Provider</th>
                <th className="px-3 py-2">Route</th>
                <th className="px-3 py-2 text-right">Reserved</th>
                <th className="px-3 py-2 text-right">Buyer paid</th>
                <th className="px-3 py-2 text-right">You earned</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2 text-right">Updated</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((s) => (
                <tr key={s.settlement_id} className="border-t hairline">
                  <td className="px-3 py-2 font-mono text-[11px]">{s.reference}</td>
                  <td className="px-3 py-2">{s.provider}</td>
                  <td className="px-3 py-2"><RouteBadge routeType={s.route_type} /></td>
                  <td className="px-3 py-2 text-right font-mono text-muted-foreground">{formatMicros(s.reserved_amount_micros, { suffix: "" })}</td>
                  <td className="px-3 py-2 text-right font-mono">{formatMicros(s.final_buyer_debit_micros, { suffix: "" })}</td>
                  <td className="px-3 py-2 text-right font-mono font-semibold text-[var(--success)]">{formatMicros(s.provider_earnings_micros, { suffix: "" })}</td>
                  <td className="px-3 py-2"><SettlementStatusPill status={s.status} /></td>
                  <td className="px-3 py-2 text-right font-mono text-muted-foreground">{s.updated}</td>
                </tr>
              ))}
              {filteredRows.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-10 text-center text-muted-foreground">No settlements match that status.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </GlassPanel>
    </div>
  );
}

/* SummaryTile defined earlier in file */

// Suppress unused — keeps RouteType import surface stable for future filters
type _Unused = RouteType;
